import React, { useEffect, useState } from "react";
import { Default } from "../../components/default/Default";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import { asinView, getAsinList, setAsin } from "../../reducer/asin/Asin.action";
import { fetchOfficeworksStores, postCodeDomainList, setZipCodeList } from "../../reducer/zipCode/ZipCode.action";
import PostRequest from "../../services/PostRequest";
import { showToaster } from "../../common/common";
import { AsinsTableNew } from "../../components/asins/Table";
import { CategoryRecords } from "../../components/asins/CategoryRecords";
import { McaDrawer } from "../../components/mca/Drawer";
import { McaMenu, McaMenuItem, McaMenuSeparator } from "../../components/mca/Menu";
import { APIRequestFields } from "../../components/APIRequestFields";
import "./asins.css";

const AddSingleDrawer = ({
  open,
  onClose,
  collectionName,
  collectionBadge,
  requestType = "amazon",
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const params = useParams();
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const _asinDetails = useSelector((state) => state.asinReducer.asinDetails);
  const _domainList = useSelector((state) => state.zipCodeReducer.domainList);
  const _zipCodesList = useSelector((state) => state.zipCodeReducer.zipCodesList);
  const _officeworksStoreList = useSelector(
    (state) => state.zipCodeReducer.officeworksStoreList
  );

  const isOfficeworks = requestType === "officeworks";
  const isRepco = requestType === "repco";
  const isSku = isOfficeworks || isRepco;
  const itemLabel = isSku ? "SKU" : "ASIN";

  useEffect(() => {
    dispatch(postCodeDomainList({ status: "active" }));
    if (isOfficeworks) {
      dispatch(fetchOfficeworksStores());
    }
  }, [dispatch, isOfficeworks]);

  useEffect(() => {
    if (!_domainList || _domainList.length === 0) return;
    if (isOfficeworks) {
      const owDomain = _domainList.find((d) => d.domain === "officeworks.com.au");
      if (owDomain) {
        dispatch(setZipCodeList({ status: "active", domain: owDomain.id }));
      }
    } else if (isRepco) {
      const repcoDomain = _domainList.find((d) => d.domain === "repco.com.au");
      if (repcoDomain) {
        dispatch(setZipCodeList({ status: "active", domain: repcoDomain.id }));
      }
    } else {
      dispatch(setZipCodeList({ status: "active" }));
    }
  }, [_domainList, requestType, dispatch, isOfficeworks, isRepco]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const formElement = e.target;
      const fd = new FormData(formElement);
      const data = {};
      fd.forEach((value, key) => {
        data[key] = value;
      });
      data.collection = params.id;

      const option = { showLoader: "add_signal_asin" };

      if (isRepco) {
        if (_asinDetails) {
          data.sku_id = _asinDetails?.id;
          await PostRequest(`/schedules/repco/sku/update/`, data, option);
        } else {
          await PostRequest(`/schedules/repco/sku/create/`, data, option);
        }
      } else if (isOfficeworks) {
        if (_asinDetails) {
          data.sku_id = _asinDetails?.id;
          await PostRequest(`/schedules/officeworks/sku/update/`, data, option);
        } else {
          await PostRequest(`/schedules/officeworks/sku/create/`, data, option);
        }
      } else {
        if (_asinDetails) {
          data.asin_id = _asinDetails?.id;
        }
        await PostRequest(
          `/schedules/asin/${_asinDetails ? "update" : "create"}/`,
          data,
          option
        );
      }

      if (_asinDetails) {
        dispatch(getAsinList({ collection_id: params.id }));
      }

      showToaster(
        _asinDetails
          ? `${itemLabel} updated successfully.`
          : `Single ${itemLabel.toLowerCase()} added successfully.`
      );

      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      showToaster(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <McaDrawer
      open={open}
      onClose={onClose}
      title={_asinDetails ? `Edit ${itemLabel}` : `Add single record`}
      subtitle={`${collectionBadge} · ${collectionName}`}
    >
      <form onSubmit={handleFormSubmit}>
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <APIRequestFields requestType={requestType} />
        </div>

        <div className="mca-drawer__foot" style={{ marginTop: "24px" }}>
          <button
            type="button"
            className="mca-btn mca-btn--outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="mca-btn mca-btn--primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : _asinDetails ? "Update" : "Add record"}
          </button>
        </div>
      </form>
    </McaDrawer>
  );
};

const AddBulkDrawer = ({ open, onClose, requestType = "amazon", onSuccess }) => {
  const params = useParams();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const isOfficeworks = requestType === "officeworks";
  const isRepco = requestType === "repco";
  const isSku = isOfficeworks || isRepco;
  const itemLabel = isSku ? "SKU" : "ASIN";

  const handleDownloadTemplate = () => {
    let sampleData, sampleName;
    if (isRepco) {
      sampleData = [
        ["sku", "customer_postcode"],
        ["A8334587", "4500"],
        ["A5685428", "3000"],
      ];
      sampleName = "repco_sample.csv";
    } else if (isOfficeworks) {
      sampleData = [
        ["sku", "customer_postcode", "store_id"],
        ["0119866", "2000", "0127"],
        ["0348540", "3000", "0001"],
      ];
      sampleName = "officeworks_sample.csv";
    } else {
      sampleData = [
        ["domain", "customer_postcode", "asin", "include_raw_html"],
        ["amazon.com.au", "4500", "B08T7LSPHS", true],
        ["amazon.com.au", "4800", "B08T7LSPHR", false],
      ];
      sampleName = "sample.csv";
    }

    const csvContent = sampleData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", sampleName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showToaster("Please select a CSV file");
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("csv", selectedFile);
      formData.append("collection_id", params.id);

      const endpoint = isRepco
        ? "/schedules/repco/sku/create/bulk/"
        : isOfficeworks
        ? "/schedules/officeworks/sku/create/bulk/"
        : "/schedules/asin/create/bulk/";

      await PostRequest(endpoint, formData, { isMultiMedia: true });

      showToaster(`Bulk ${itemLabel}s imported successfully.`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      showToaster(error.message, "error");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <McaDrawer
      open={open}
      onClose={onClose}
      title="Add records in bulk"
      subtitle="Upload a CSV of ASINs"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
        <div
          className="mca-alert mca-alert--info"
          style={{ marginBottom: "18px" }}
        >
          <svg
            className="lead"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5" />
            <path d="M12 8h.01" />
          </svg>
          <div className="mca-alert__body">
            CSV columns: <code>asin, domain, postcode, output</code>.{" "}
            <button
              type="button"
              onClick={handleDownloadTemplate}
              style={{
                background: "none",
                border: "none",
                color: "var(--mca-primary)",
                cursor: "pointer",
                textDecoration: "underline",
                fontSize: "inherit",
              }}
            >
              Download template
            </button>
          </div>
        </div>

        <div data-dropzone>
          <div className="mca-dropzone">
            <div className="mca-dropzone__icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 16V4" />
                <path d="m7 9 5-5 5 5" />
                <path d="M5 20h14" />
              </svg>
            </div>
            <div className="mca-dropzone__title">Drop your CSV here, or click to browse</div>
            <div className="mca-dropzone__hint">Up to 10,000 rows · max 5 MB</div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: "pointer",
                top: 0,
                left: 0,
              }}
            />
          </div>
          {selectedFile && (
            <div className="mca-dropzone__file">
              <svg
                className="fic"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 2.5h8l4 4V21a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1Z" />
                <path d="m9 14 2 2 4-4" />
              </svg>
              <div style={{ flex: 1 }}>
                <div className="nm">{selectedFile.name}</div>
                <div className="mt">{Math.round(selectedFile.size / 1024)} KB · ready to import</div>
              </div>
              <button
                type="button"
                className="mca-iconbtn"
                style={{ width: "32px", height: "32px" }}
                onClick={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                aria-label="Remove"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mca-drawer__foot" style={{ marginTop: "24px" }}>
        <button
          type="button"
          className="mca-btn mca-btn--outline"
          onClick={onClose}
          disabled={isUploading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="mca-btn mca-btn--primary"
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
        >
          {isUploading ? "Importing..." : "Import CSV"}
        </button>
      </div>
    </McaDrawer>
  );
};

export const Asins = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const _asinList = useSelector((state) => state.asinReducer.asinList);
  const _asinBar = useSelector((state) => state.asinReducer.asinBar);

  const [collectionDetails, setCollectionDetails] = useState({
    name: "Collection",
    badge: "Amazon",
  });
  const [requestType, setRequestType] = useState("amazon");
  const [showAddSingle, setShowAddSingle] = useState(false);
  const [showAddBulk, setShowAddBulk] = useState(false);
  const [categoryResult, setCategoryResult] = useState(null);

  const isOfficeworks = requestType === "officeworks";
  const isRepco = requestType === "repco";
  const isCategory = requestType === "amazon_category_scrape";
  const itemLabel = isOfficeworks || isRepco ? "SKU" : "ASIN";

  const loadCollectionDetails = async () => {
    try {
      const response = await PostRequest("/schedules/get/", {
        schedule_id: params.id,
      });
      if (response?.request_type) {
        setRequestType(response.request_type);
      }
      if (response?.name) {
        setCollectionDetails((prev) => ({
          ...prev,
          name: response.name,
          badge:
            response.request_type === "officeworks"
              ? "Officeworks"
              : response.request_type === "repco"
              ? "Repco"
              : "Amazon",
        }));
      }
    } catch (error) {
      console.log("Error fetching collection details:", error.message);
    }
  };

  useEffect(() => {
    loadCollectionDetails();
  }, [params.id]);

  useEffect(() => {
    if (requestType === "officeworks") {
      dispatch(fetchOfficeworksStores());
    }
  }, [requestType, dispatch]);

  const loadCategoryResult = async () => {
    try {
      const res = await PostRequest("/schedules/amazon/category/result/", {
        collection_id: params.id,
      });
      setCategoryResult(Array.isArray(res) ? res[0] : res);
    } catch (error) {
      console.log("Error fetching category result:", error.message);
    }
  };

  useEffect(() => {
    if (isCategory) loadCategoryResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCategory, params.id]);

  const handleAddSuccess = () => {
    dispatch(getAsinList({ collection_id: params.id }));
    dispatch(asinView(false));
    dispatch(setAsin(false));
  };

  return (
    <Default>
      <div className="mca-content">
        <button
          type="button"
          className="mca-btn mca-btn--ghost mca-btn--sm"
          onClick={() => navigate(-1)}
          style={{ marginBottom: "12px", paddingLeft: "8px" }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m15 6-6 6 6 6" />
          </svg>
          <span className="mca-btn__label">Back</span>
        </button>

        {/* Breadcrumb */}
        <nav className="mca-crumbs" style={{ marginBottom: "14px" }}>
          <Link to="/collections">Collections</Link>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m9 6 6 6-6 6" />
          </svg>
          <span className="current">{collectionDetails.name}</span>
        </nav>

        {/* Page header */}
        <div className="rec-head">
          <div>
            <h1 style={{
              fontSize: "26px",
              fontWeight: "700",
              letterSpacing: "-0.02em",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
            >
              {collectionDetails.name}
              <span className="mca-badge mca-badge--soft-primary">
                {collectionDetails.badge}
              </span>
            </h1>
            <p
              style={{
                color: "var(--mca-muted)",
                fontSize: "14px",
                margin: "6px 0 0",
              }}
            >
              {isCategory
                ? `${categoryResult?.asin_count ?? categoryResult?.progress?.asins_total ?? 0} products`
                : `${_asinList.length} records`}
            </p>
          </div>
          {!isCategory && (
          <div className="rec-actions">
            <div className="mca-input-group" style={{ width: "220px" }}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4-4" />
              </svg>
              <input
                className="mca-input"
                type="search"
                placeholder={`Search ${itemLabel}...`}
                style={{ paddingTop: "8px", paddingBottom: "8px" }}
              />
            </div>
            <button
              className="mca-btn mca-btn--outline"
              onClick={() => setShowAddBulk(true)}
              type="button"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3v12" />
                <path d="m8 7 4-4 4 4" />
                <path d="M4 19h16" />
              </svg>
              Add bulk
            </button>
            <button
              className="mca-btn mca-btn--primary"
              onClick={() => {
                dispatch(asinView(false));
                setShowAddSingle(true);
              }}
              type="button"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add single
            </button>
          </div>
          )}
        </div>

        {/* Records table */}
        {isCategory ? (
          <CategoryRecords result={categoryResult} onRefresh={loadCategoryResult} />
        ) : (
          <AsinsTableNew
            requestType={requestType}
            onEdit={() => {
              setShowAddSingle(true);
            }}
          />
        )}
      </div>

      {/* Add Single drawer */}
      <AddSingleDrawer
        open={showAddSingle}
        onClose={() => setShowAddSingle(false)}
        collectionName={collectionDetails.name}
        collectionBadge={collectionDetails.badge}
        requestType={requestType}
        onSuccess={handleAddSuccess}
      />

      {/* Add Bulk drawer */}
      <AddBulkDrawer
        open={showAddBulk}
        onClose={() => setShowAddBulk(false)}
        requestType={requestType}
        onSuccess={handleAddSuccess}
      />
    </Default>
  );
};
